import { useState } from "react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { ACCENT_PALETTE, resolveAccentPaletteColor } from "~/lib/accentPalette";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import { PartialProject, projectPath, updateProject } from "~/services/projects";

interface ProjectAppearanceSectionProps {
  project: PartialProject;
}

export const ProjectAppearanceSection = ({ project }: ProjectAppearanceSectionProps) => {
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // The last-saved color, used both as the Reset target and to decide whether the
  // form is dirty. Updated on a successful save so Save/Reset re-disable immediately,
  // without waiting on the parent to re-fetch and pass down a fresh `project` prop.
  const [committedColor, setCommittedColor] = useState(
    () => resolveAccentPaletteColor(project.accent_color ?? undefined).accent
  );
  const [color, setColor] = useState(committedColor);

  const isDirty = color !== committedColor;
  const previewColor = resolveAccentPaletteColor(color);

  const handleReset = () => setColor(committedColor);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateProject(project.id, { accent_color: color });
      setCommittedColor(color);
      await mutate(projectPath(project.id));
      showSuccess("Appearance saved");
    } catch {
      showError("Failed to save appearance");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsSection title="Appearance">
      <div className="grid grid-cols-[280px_1fr] gap-8 px-4 py-4">
        <div className="flex flex-col items-center gap-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Preview
          </span>
          <div className="relative h-[246px] w-[200px] overflow-hidden rounded-xl rounded-l-lg border border-border bg-card">
            <div
              className="absolute inset-y-0 left-0 w-8 overflow-hidden rounded-l-lg"
              style={{ backgroundColor: previewColor.bg }}
            />
            <div className="ml-8 flex h-full flex-col gap-1 px-3 py-4">
              <p className="truncate text-sm font-semibold text-card-foreground">{project.name}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Color</p>
            <div className="flex flex-wrap gap-2.5">
              {ACCENT_PALETTE.map((swatch) => (
                <button
                  key={swatch.accent}
                  type="button"
                  aria-label={`Use color ${swatch.name}`}
                  aria-pressed={color === swatch.accent}
                  onClick={() => setColor(swatch.accent)}
                  className="size-7 cursor-pointer rounded-full border-2"
                  style={{
                    backgroundColor: swatch.bg,
                    borderColor: swatch.accent,
                    boxShadow:
                      color === swatch.accent
                        ? `0 0 0 2px var(--background), 0 0 0 4px ${swatch.accent}`
                        : undefined,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 px-4 py-3">
        <Button variant="ghost" onClick={handleReset} disabled={!isDirty || isSaving}>
          Reset
        </Button>
        <Button onClick={handleSave} disabled={!isDirty || isSaving} loading={isSaving}>
          Save
        </Button>
      </div>
    </SettingsSection>
  );
};
